import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export type LoomOnboardingButtonAppearance = 'default' | 'primary';

export type LoomOnboardingButtonProps = {
	children: ReactNode;
	appearance?: LoomOnboardingButtonAppearance;
	onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
	type?: 'button' | 'submit' | 'reset';
	fullWidth?: boolean;
	disabled?: boolean;
	isActive?: boolean;
};

// Base chrome + label typography shared across appearances. Styles are
// applied directly to the <button> element so we don't need descendant
// selectors (which would trip the
// `@atlaskit/ui-styling-standard/no-nested-selectors` rule) or
// `!important` overrides against AtlasKit's internal button element.
const baseCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '6px',
	borderWidth: 0,
	borderStyle: 'solid',
	paddingBlock: '8px',
	paddingInline: '16px',
	margin: 0,
	fontFamily: 'var(--atlassian-sans-font-family)',
	fontSize: '14px',
	lineHeight: '1.5rem',
	fontWeight: token('font.weight.medium'),
	textAlign: 'center',
	cursor: 'pointer',
	transition: 'background-color 100ms ease-out',
	// Standard focus ring, offset a couple pixels off the button so it's
	// visible even when the button colour matches the ring (primary button
	// uses the same blue as `color.border.focused`).
	':focus': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: '2px',
	},
	':focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: '2px',
	},
	':disabled': {
		cursor: 'not-allowed',
		opacity: 0.5,
	},
});

const defaultAppearanceCss = css({
	backgroundColor: token('color.background.neutral'),
	color: token('color.text'),
	':hover': {
		backgroundColor: token('color.background.neutral.hovered'),
	},
	':active': {
		backgroundColor: token('color.background.neutral.pressed'),
	},
});

const primaryAppearanceCss = css({
	backgroundColor: token('color.background.brand.bold'),
	color: token('color.text.inverse'),
	':hover': {
		backgroundColor: token('color.background.brand.bold.hovered'),
	},
	':active': {
		backgroundColor: token('color.background.brand.bold.pressed'),
	},
});

const fullWidthCss = css({
	width: '100%',
});

const defaultActiveCss = css({
	backgroundColor: token('color.background.neutral.pressed'),
});

const primaryActiveCss = css({
	backgroundColor: token('color.background.brand.bold.pressed'),
});

/**
 * Reusable CTA button for the Loom onboarding flow.
 *
 * Renders a native <button> styled with Compiled CSS to match the shared
 * Atlassian Sans 14/500 label + 6px pill chrome used across the loom
 * onboarding views (RecorderCard, DownloadOptionsStep, etc). Styling is
 * applied directly to the element so we avoid nested-selector escape
 * hatches and !important overrides that were previously needed to force
 * styles onto AtlasKit's legacy <Button> internals.
 *
 * Pass `fullWidth` when the button lives inside a card / column that
 * should stretch it to 100% width.
 */
export const LoomOnboardingButton = ({
	children,
	appearance = 'default',
	onClick,
	type = 'button',
	fullWidth = false,
	disabled = false,
	isActive = false,
}: LoomOnboardingButtonProps) => (
	<button
		type={type}
		onClick={onClick}
		disabled={disabled}
		css={[
			baseCss,
			appearance === 'primary' ? primaryAppearanceCss : defaultAppearanceCss,
			fullWidth && fullWidthCss,
			isActive && appearance === 'primary' && primaryActiveCss,
			isActive && appearance !== 'primary' && defaultActiveCss,
		]}
	>
		{children}
	</button>
);
